import { inject, Signal } from '@angular/core';
import { injectMutation } from '@tanstack/angular-query-experimental';
import { firstValueFrom } from 'rxjs';

import { FileManagerComponent } from '@shared/components/file-manager/file-manager.component';
import { File } from '@shared/components/file-manager/state/file.model';
import { DocumentType, EntityType, EventType } from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { AuthQuery } from '@shared/store/auth/auth.query';
import { EventService } from '@shared/store/event/event.service';
import { MainQuery } from '@shared/store/main/main.query';
import { Utils } from '@shared/utils/utils';

export const injectUploadSiteBudgetDocumentsMutation = (
  fileManager: Signal<FileManagerComponent | undefined>
) => {
  const mainQuery = inject(MainQuery);
  const overlayService = inject(OverlayService);

  return injectMutation(() => ({
    mutationFn: ({
      siteBudgetVersionId,
      siteId,
    }: {
      siteBudgetVersionId: string;
      siteId: string;
    }) => {
      if (!fileManager()) {
        throw new Error('Missing file manager context');
      }

      const trialKey = mainQuery.trialKey();

      return fileManager()!.fileService?.uploadFiles({
        filterLocalFilesByUploadedFlag: true,
        insertDocument: true,
        is_metadata_editable: false,
        metadata: {
          documentType: DocumentType.DOCUMENT_SITE_AGREEMENTS,
          entity_id: siteBudgetVersionId,
          entity_type_id: EntityType.SITE_BUDGET_VERSION,
          site: siteId,
        },
        overrideFilePath: `trials/${trialKey}/sites/${siteId}/${siteBudgetVersionId}/contracts/`,
        removeFilesOnPartialFailure: true,
        showSuccess: false,
      });
    },
    onError: (error: string[]) => {
      overlayService.error(error);
    },
  }));
};

export const injectProcessSiteDocumentEventMutation = () => {
  const eventService = inject(EventService);
  const authQuery = inject(AuthQuery);

  return injectMutation(() => ({
    mutationFn: ({
      files,
      message,
      siteBudgetVersionId,
      siteId,
    }: {
      files: File[];
      message: string;
      siteBudgetVersionId: string;
      siteId: string;
    }) => {
      return firstValueFrom(
        eventService.processEvent$({
          entity_id: '',
          entity_type: EntityType.DOCUMENT,
          payload: JSON.stringify({
            file_names: files.map((file) => file.fileName).join(', ') || Utils.zeroHyphen,
            reason_for_amendment: message,
            site_budget_version_id: siteBudgetVersionId,
            site_id: siteId,
            uploaded_by: `${authQuery.getFullName()} ${authQuery.getEmail()}`,
          }),
          type: EventType.SITE_CONTRACT_UPLOADED,
        })
      );
    },
  }));
};
